import { TerminalLog } from '@/components/TerminalLog';
import { terminalLogs } from '@/data/terminalLogs';
import { useTerminalSimulation } from '@/hooks/useTerminalSimulation';
import { triggerButtonHaptic } from '@/utils/haptics';
import { playClickSound } from '@/utils/sounds';
import { motion } from 'framer-motion';
import { useCallback, useEffect } from 'react';

interface TerminalBlockProps {
  onDeploy: () => void;
}

export function TerminalBlock({ onDeploy }: TerminalBlockProps) {
  const { allMessages, currentMessage, isComplete, skip } = useTerminalSimulation({
    messages: terminalLogs,
  });

  const handleDeploy = useCallback(() => {
    playClickSound();
    triggerButtonHaptic();
    // Scroll to next section (Notion)
    const notionSection = document.querySelector('[aria-label="SELECT * FROM Vova - База Знаний"]') as HTMLElement;
    if (notionSection) {
      notionSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleSkip = useCallback(() => {
    if (!isComplete) {
      skip();
    }
    // Small delay to ensure skip completes before deploy
    setTimeout(() => {
      playClickSound();
      triggerButtonHaptic();
      onDeploy();
    }, 100);
  }, [isComplete, skip, onDeploy]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't capture space if user is typing in an input or textarea
      const activeElement = document.activeElement;
      const isTyping = activeElement instanceof HTMLInputElement || 
                       activeElement instanceof HTMLTextAreaElement ||
                       activeElement?.getAttribute('contenteditable') === 'true';
      
      if (isTyping) return;
      
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSkip]);

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-[#1E1E1E] flex flex-col items-center justify-center p-4 sm:p-8 font-terminal"
      aria-label="System Initialization Terminal"
    >
      <div className="w-full max-w-4xl space-y-4">
        {allMessages.map((msg, idx) => (
          <TerminalLog key={idx} level={msg.level} text={msg.text} displayedText={msg.text} />
        ))}
        {currentMessage && (
          <TerminalLog
            level={currentMessage.level}
            text={currentMessage.text}
            displayedText={currentMessage.displayedText}
          />
        )}
      </div>

      {isComplete && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleDeploy}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-[#6A8759] text-white font-bold rounded-sm hover:bg-[#7A9769] transition-colors focus:outline-none focus:ring-2 focus:ring-[#6A8759] focus:ring-offset-2 focus:ring-offset-[#1E1E1E] text-sm sm:text-base min-h-[44px]"
            aria-label="Run deployment to proceed to next section"
          >
            RUN DEPLOYMENT
          </motion.button>
        </motion.div>
      )}
    </motion.section>
  );
}
